import type { WatchablesFactory } from '../../watchables/watchables-factory';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { WorkflowGraphApiData } from '../workflow-graph.types';
import {
  DEFAULT_WORKFLOW_RENDER_CATALOG,
  listModelDescriptors,
  type WorkflowDisposable,
  type WorkflowNodeModelFactoryContext,
  type WorkflowRenderCatalog,
  type WorkflowRenderableNodeModelMode,
} from '../workflow-render-catalog';
import { WorkflowAgentNodeModel } from './workflow-agent-node.model';

export {
  readWebhookHasSecretFromGraph,
  readWebhookInstallStatusFromGraph,
  readWebhookPreviewStateFromGraph,
} from './webhook-trigger-graph';

type WorkflowNodeModelRegistryProps = {
  catalog?: WorkflowRenderCatalog;
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    | 'makeWebhookTriggerInstallStateWatchable'
    | 'makeAgentNodeConfigWatchable'
    | 'releaseAgentNodeConfigWatchable'
  >;
  isPreviewEntity: (entityId: string) => boolean;
};

type ModelSlot = {
  descriptorKey: string;
  mode: WorkflowRenderableNodeModelMode;
  model: WorkflowDisposable;
};

function modelMapKey(descriptorKey: string, entityId: string): string {
  return `${descriptorKey}:${entityId}`;
}

function releaseAgentNodeModelWatchable(model: WorkflowDisposable): void {
  if (model instanceof WorkflowAgentNodeModel) {
    model.releaseWatchable();
  }
}

/**
 * Lazily creates and disposes per-entity view models declared on a
 * {@link WorkflowRenderCatalog}.
 */
export class WorkflowNodeModelRegistry {
  private readonly _catalog: WorkflowRenderCatalog;
  private readonly _client: Pick<TenantedClient, 'resource'>;
  private readonly _watchablesFactory: WorkflowNodeModelRegistryProps['watchablesFactory'];
  private readonly _isPreviewEntity: (entityId: string) => boolean;
  private readonly _models = new Map<string, ModelSlot>();
  private _graph: WorkflowGraphApiData | null = null;

  constructor(props: WorkflowNodeModelRegistryProps) {
    this._catalog = props.catalog ?? DEFAULT_WORKFLOW_RENDER_CATALOG;
    this._client = props.client;
    this._watchablesFactory = props.watchablesFactory;
    this._isPreviewEntity = props.isPreviewEntity;
  }

  get catalog(): WorkflowRenderCatalog {
    return this._catalog;
  }

  private factoryContext(): WorkflowNodeModelFactoryContext {
    return {
      client: this._client,
      watchablesFactory: this._watchablesFactory,
      graph: this._graph,
      isPreviewEntity: this._isPreviewEntity,
    };
  }

  syncGraph(graph: WorkflowGraphApiData): void {
    this._graph = graph;
    const ctx = this.factoryContext();

    const nextKeys = new Set<string>();
    for (const descriptor of listModelDescriptors(this._catalog)) {
      const models = descriptor.models;
      if (models === undefined) {
        continue;
      }
      for (const entityId of models.listEntityIdsInGraph(graph)) {
        nextKeys.add(modelMapKey(descriptor.key, entityId));
      }
    }

    for (const [key, slot] of this._models) {
      if (!nextKeys.has(key)) {
        releaseAgentNodeModelWatchable(slot.model);
        slot.model.dispose();
        this._models.delete(key);
      }
    }

    for (const descriptor of listModelDescriptors(this._catalog)) {
      const models = descriptor.models;
      if (models === undefined) {
        continue;
      }
      for (const entityId of models.listEntityIdsInGraph(graph)) {
        this.getModel(descriptor.key, entityId);
      }
    }

    for (const descriptor of listModelDescriptors(this._catalog)) {
      const models = descriptor.models;
      if (models === undefined || models.syncEntityFromGraph === undefined) {
        continue;
      }
      for (const entityId of models.listEntityIdsInGraph(graph)) {
        const slot = this._models.get(modelMapKey(descriptor.key, entityId));
        if (slot === undefined) {
          continue;
        }
        models.syncEntityFromGraph(ctx, entityId, slot.model, graph);
      }
    }
  }

  getModel<TModel extends WorkflowDisposable>(
    descriptorKey: string,
    entityId: string
  ): TModel {
    const existing = this._models.get(modelMapKey(descriptorKey, entityId));
    const descriptor = listModelDescriptors(this._catalog).find(
      (entry) => entry.key === descriptorKey
    );
    if (descriptor?.models === undefined) {
      throw new Error(
        `No view models registered for workflow render node "${descriptorKey}"`
      );
    }

    const ctx = this.factoryContext();
    const wantsPreview =
      descriptor.models.wantsPreview?.(ctx, entityId) ??
      descriptor.models.createPreviewModel !== undefined
        ? ctx.isPreviewEntity(entityId)
        : false;

    if (existing !== undefined) {
      const modeMatches =
        (wantsPreview && existing.mode === 'preview') ||
        (!wantsPreview && existing.mode === 'full');
      if (modeMatches) {
        return existing.model as TModel;
      }
      releaseAgentNodeModelWatchable(existing.model);
      existing.model.dispose();
      this._models.delete(modelMapKey(descriptorKey, entityId));
    }

    if (wantsPreview) {
      if (descriptor.models.createPreviewModel === undefined) {
        throw new Error(
          `Workflow render node "${descriptorKey}" has no preview model factory`
        );
      }
      const model = descriptor.models.createPreviewModel(ctx, entityId);
      this._models.set(modelMapKey(descriptorKey, entityId), {
        descriptorKey,
        mode: 'preview',
        model,
      });
      return model as TModel;
    }

    const model = descriptor.models.createFullModel(ctx, entityId);
    this._models.set(modelMapKey(descriptorKey, entityId), {
      descriptorKey,
      mode: 'full',
      model,
    });
    return model as TModel;
  }

  /** Canvas-selected entity view model (set via actions {@link targetModel}). */
  getTargetedNodeModel(): WorkflowDisposable | null {
    for (const slot of this._models.values()) {
      if (slot.model.isTargeted === true) {
        return slot.model;
      }
    }
    return null;
  }

  dispose(): void {
    for (const slot of this._models.values()) {
      releaseAgentNodeModelWatchable(slot.model);
      slot.model.dispose();
    }
    this._models.clear();
    this._graph = null;
  }
}
